import { defaultAgeAnonymiser, preserveAnonymiser } from "@thalamos/common";

import { FormSectionSchema } from "../types/FormSection.js";
import { IncidentFormTypeSchema } from "../types/incidentWorkItem.js";
import {
  type CreateIncidentMinimumFields,
  type DetentionDetailsMinimumFields,
  type FinishFormMinimumFields,
  type InformationForHealthcareMinimumFields,
  type McaAccompanimentMinimumFields,
  type McaCapacityAssessmentMinimumFields,
  type McaIncidentDetailsMinimumFields,
  type McaPersonDetailsMinimumFields,
  type PersonDetailsMinimumFields,
  type RecordPlaceOfSafetyArrivalMinimumFields,
  type RecordPlaceOfSafetyLeaveMinimumFields,
  type RecordPlaceOfSafetyMinimumFields,
  type RecordPolicePresenceMinimumFields,
  type RecordPreEndIncidentMinimumFields,
  type RecordShareIncidentMinimumFields,
  type RequestPolicePresenceMinimumFields,
  type TriageIncidentMinimumFields,
} from "../types/workflow/index.js";
import type {
  FormAnonymiser,
  IncidentWorkItemAnonymiser,
  SectionAnonymiser,
} from "./IncidentWorkItemAnonymiser.js";
import { locationFuzzerAnonymiser } from "./util.js";

/*
  ShareIncidentFormData
  */

const createIncidentMainAnonymiser: SectionAnonymiser<CreateIncidentMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.createIncidentMain,
    fieldAnonymisers: {
      dispatchNumber: preserveAnonymiser,
      commandUnit: preserveAnonymiser,
    },
  };

export const createIncidentDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.createIncident,
  sectionAnonymisers: [createIncidentMainAnonymiser],
};

const triageIncidentMainAnonymiser: SectionAnonymiser<TriageIncidentMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.triageMain,
    fieldAnonymisers: {
      isHealthcareProfessionalConsulted: preserveAnonymiser,
      whatWillYouDo: preserveAnonymiser,
    },
  };

export const triageIncidentDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.triage,
  sectionAnonymisers: [triageIncidentMainAnonymiser],
};

const personDetailstMainAnonymiser: SectionAnonymiser<PersonDetailsMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.personDetails,
    fieldAnonymisers: {
      personAddress: locationFuzzerAnonymiser,
      dateOfBirth: defaultAgeAnonymiser,
      satisfiedPersonIsOver18: preserveAnonymiser,
      hasRelativeOrFriend: preserveAnonymiser,
    },
  };

const detentionDetailsAnonymiser: SectionAnonymiser<DetentionDetailsMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.detentionDetails,
    fieldAnonymisers: {
      custodyNumber: preserveAnonymiser,
      dateTimeOfDetention: preserveAnonymiser,
    },
  };

const informationForHealthcareAnonymiser: SectionAnonymiser<InformationForHealthcareMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.informationForHealthcare,
    fieldAnonymisers: {},
  };

export const section136DefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.section136,
  sectionAnonymisers: [
    personDetailstMainAnonymiser,
    detentionDetailsAnonymiser,
    informationForHealthcareAnonymiser,
  ],
};

const mcaPersonDetailsAnonymiser: SectionAnonymiser<McaPersonDetailsMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.mcaPersonDetails,
    fieldAnonymisers: {
      personAddress: locationFuzzerAnonymiser,
      dateOfBirth: defaultAgeAnonymiser,
      satisfiedPersonIsOver16: preserveAnonymiser,
      ethnicAppearance: preserveAnonymiser,
      hasRelativeOrFriend: preserveAnonymiser,
    },
  };

const mcaIncidentDetailsAnonymiser: SectionAnonymiser<McaIncidentDetailsMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.mcaIncidentDetails,
    fieldAnonymisers: {
      dateTimeOfIncident: preserveAnonymiser,
    },
  };

const mcaCapacityAssessmentAnonymiser: SectionAnonymiser<McaCapacityAssessmentMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.mcaCapacityAssessment,
    fieldAnonymisers: {
      canUnderstandInformation: preserveAnonymiser,
      canRetainInformation: preserveAnonymiser,
      canWeighInformation: preserveAnonymiser,
      canUseInformation: preserveAnonymiser,
      canCommunicateDecision: preserveAnonymiser,
      impairmentDisturbance: preserveAnonymiser,
      lackOfCapacity: preserveAnonymiser,
      restraintNecessary: preserveAnonymiser,
      bestInterestPhysicalHealthCondition: preserveAnonymiser,
    },
  };

const mcaAccompanimentAnonymiser: SectionAnonymiser<McaAccompanimentMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.mcaAccompaniment,
    fieldAnonymisers: {
      accompanyingToEmergencyDepartment: preserveAnonymiser,
    },
  };

export const mentalCapacityActDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.mentalCapacityAct,
  sectionAnonymisers: [
    mcaPersonDetailsAnonymiser,
    mcaIncidentDetailsAnonymiser,
    mcaCapacityAssessmentAnonymiser,
    mcaAccompanimentAnonymiser,
  ],
};

const recordPlaceOfSafetyMainAnonymiser: SectionAnonymiser<RecordPlaceOfSafetyMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyMain,
    fieldAnonymisers: {
      placeOfSafety: preserveAnonymiser,
    },
  };

const recordPlaceOfSafetyArriveAnonymiser: SectionAnonymiser<RecordPlaceOfSafetyArrivalMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyArrival,
    fieldAnonymisers: {
      arrivalDateTime: preserveAnonymiser,
      didNotArrive: preserveAnonymiser,
    },
  };

const recordPlaceOfSafetyLeaveAnonymiser: SectionAnonymiser<RecordPlaceOfSafetyLeaveMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.recordPlaceOfSafetyLeave,
    fieldAnonymisers: {
      didYouLeave: preserveAnonymiser,
    },
  };

export const recordPlaceOfSafetyDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.recordPlaceOfSafety,
  sectionAnonymisers: [
    recordPlaceOfSafetyMainAnonymiser,
    recordPlaceOfSafetyArriveAnonymiser,
    recordPlaceOfSafetyLeaveAnonymiser,
  ],
};

const preEndIncidentAnonymiser: SectionAnonymiser<RecordPreEndIncidentMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.preEndIncident,
    fieldAnonymisers: {},
  };

export const preEndIncidentDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.preEndIncident,
  sectionAnonymisers: [preEndIncidentAnonymiser],
};

const finishFormAnonymiser: SectionAnonymiser<FinishFormMinimumFields> = {
  sectionId: FormSectionSchema.enum.finishForm,
  fieldAnonymisers: {
    dateTimeLeft: preserveAnonymiser,
  },
};

export const finishFormDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.finishForm,
  sectionAnonymisers: [finishFormAnonymiser],
};

const requestPolicePresenceMainAnonymiser: SectionAnonymiser<RequestPolicePresenceMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.requestPolicePresenceMain,
    fieldAnonymisers: {
      incidentPlaceOfSafetyId: preserveAnonymiser,
      hasPolicePresenceRequested: preserveAnonymiser,
      policePresenceRequestedReason: preserveAnonymiser,
      placeOfSafety: preserveAnonymiser,

      emhaUserId: preserveAnonymiser,
      emhaTeamId: preserveAnonymiser,
      emhaTeamName: preserveAnonymiser,
      emhaUserName: preserveAnonymiser,
      emhaOrganisationId: preserveAnonymiser,
      emhaOrganisationName: preserveAnonymiser,
      mhfUserName: preserveAnonymiser,
      createdAt: preserveAnonymiser,
    },
  };

const recordPolicePresenceAnonymiser: SectionAnonymiser<RecordPolicePresenceMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.recordPolicePresence,
    fieldAnonymisers: {
      hasRemainedAtPlaceOfSafety: preserveAnonymiser,
      officerName: preserveAnonymiser,
      createdAt: preserveAnonymiser,
    },
  };

export const requestPolicePresenceDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.requestPolicePresence,
  sectionAnonymisers: [
    requestPolicePresenceMainAnonymiser,
    recordPolicePresenceAnonymiser,
  ],
};

const shareIncidentAnonymiser: SectionAnonymiser<RecordShareIncidentMinimumFields> =
  {
    sectionId: FormSectionSchema.enum.shareIncident,
    fieldAnonymisers: {
      // TODO: do we preserve thsi or not
      email: preserveAnonymiser,
    },
  };

export const shareIncidentDefaultAnonymiser: FormAnonymiser = {
  formType: IncidentFormTypeSchema.enum.shareIncident,
  sectionAnonymisers: [shareIncidentAnonymiser],
};

export const defaultAnonymiser: IncidentWorkItemAnonymiser = {
  coreIncidentAnonymiser: (incidentWorkItem) => incidentWorkItem,
  formAnonymisers: [
    // core data
    createIncidentDefaultAnonymiser,
    triageIncidentDefaultAnonymiser,
    section136DefaultAnonymiser,
    mentalCapacityActDefaultAnonymiser,

    // eMHA interactions
    recordPlaceOfSafetyDefaultAnonymiser,
    requestPolicePresenceDefaultAnonymiser,

    // end incident
    preEndIncidentDefaultAnonymiser,
    finishFormDefaultAnonymiser,
    shareIncidentDefaultAnonymiser,
  ],
};
